import React, { createContext, useContext, useEffect, useState } from "react";
import { db } from "../firebase/config";
import {
  doc,
  collection,
  getDoc,
  setDoc,
  deleteDoc,
  addDoc,
  onSnapshot,
  getDocs,
  updateDoc,
  increment,
} from "firebase/firestore";
import { useAuth } from "./AuthContext";

export const FinanceContext = createContext();

const MAX_AMOUNT = 10000000;

const generateTransactionId = () => {
  try {
    if (
      typeof window !== "undefined" &&
      window.crypto &&
      typeof window.crypto.randomUUID === "function"
    ) {
      return window.crypto.randomUUID();
    }
  } catch {
    // ignore
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const ensureTransactionIds = (history = []) => {
  let changed = false;
  const next = (history || []).map((h) => {
    if (h && (h.id === 0 || h.id)) return h;
    changed = true;
    return { ...h, id: generateTransactionId() };
  });
  return { history: next, changed };
};

const normalizeTransactionAmounts = (history = []) => {
  let changed = false;
  const next = (history || []).map((h) => {
    if (!h) return h;
    const num = Number(h.amount);
    if (!Number.isFinite(num)) {
      if (h.amount === 0 || h.amount === "0") return h;
      changed = true;
      return { ...h, amount: 0 };
    }
    const normalized = Math.abs(num);
    if (normalized === num) return h;
    changed = true;
    return { ...h, amount: normalized };
  });
  return { history: next, changed };
};

export function FinanceProvider({ children }) {
  const { currentUser } = useAuth();
  const [history, setHistory] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expenseLimit, setExpenseLimit] = useState(null);

  const defaultCategories = [
    { name: "Food / Drinks" },
    { name: "Home / Rent" },
    { name: "Car & Fuel" },
    { name: "Entertainment" },
    { name: "Clothes" },
    { name: "Education" },
    { name: "Gifts" },
    { name: "Travel" },
    { name: "Health" },
    { name: "Beauty" },
  ];

  useEffect(() => {
    if (!currentUser) return;

    const financeDocRef = doc(db, "users", currentUser.uid, "finance", "data");
    const categoriesRef = collection(financeDocRef, "categories");

    const unsubFinance = onSnapshot(financeDocRef, async (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        const { history: withIds, changed: idsChanged } = ensureTransactionIds(
          data.history || []
        );
        const { history: normalizedHistory, changed: amountsChanged } =
          normalizeTransactionAmounts(withIds);

        setHistory(normalizedHistory);
        if (data.expenseLimit) setExpenseLimit(data.expenseLimit);

        // One-time migration: persist ids / amount normalization for older entries.
        if (idsChanged || amountsChanged) {
          try {
            await setDoc(
              doc(db, "users", currentUser.uid, "finance", "data"),
              { history: normalizedHistory },
              { merge: true }
            );
          } catch {
            // Non-fatal: UI will still work for this session
          }
        }
      }
      setLoading(false);
    });

    (async () => {
      const catsSnap = await getDocs(categoriesRef);
      if (catsSnap.empty) {
        for (const cat of defaultCategories) {
          await addDoc(categoriesRef, cat);
        }
      }
    })();

    const unsubCats = onSnapshot(categoriesRef, (snap) => {
      setCategories(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });

    return () => {
      unsubFinance();
      unsubCats();
    };
  }, [currentUser]);

  const updateExpenseLimit = async (limit, period) => {
    if (!currentUser) return;
    const newLimit = { limit: Number(limit), period };
    await setDoc(
      doc(db, "users", currentUser.uid, "finance", "data"),
      { expenseLimit: newLimit },
      { merge: true }
    );
  };

  const deleteExpenseLimit = async () => {
    if (!currentUser) return;
    await setDoc(
      doc(db, "users", currentUser.uid, "finance", "data"),
      { expenseLimit: null },
      { merge: true }
    );
  };

  const addIncome = async (amount, note = "") => {
    const numAmount = Math.abs(Number(amount));
    if (numAmount > MAX_AMOUNT) return alert(`Maximum amount is ${MAX_AMOUNT}`);

    const newEntry = {
      id: generateTransactionId(),
      type: "income",
      amount: numAmount,
      note,
      date: new Date().toISOString(),
    };

    const newHistory = [...history, newEntry];
    const dataRef = doc(db, "users", currentUser.uid, "finance", "data");
    await setDoc(dataRef, { history: newHistory }, { merge: true });
    await updateDoc(dataRef, {
      "totals.totalIncome": increment(numAmount),
    }).catch(() => {
      setDoc(
        dataRef,
        {
          totals: {
            totalIncome: numAmount,
            totalExpenses: 0,
            totalAllocated: 0,
          },
        },
        { merge: true }
      );
    });
  };

  const addSpending = async (amount, category, note = "") => {
    const numAmount = Math.abs(Number(amount));
    if (numAmount > MAX_AMOUNT) return alert(`Maximum amount is ${MAX_AMOUNT}`);

    const newEntry = {
      id: generateTransactionId(),
      type: "spending",
      amount: numAmount,
      category,
      note,
      date: new Date().toISOString(),
    };

    const newHistory = [...history, newEntry];
    const dataRef = doc(db, "users", currentUser.uid, "finance", "data");
    await setDoc(dataRef, { history: newHistory }, { merge: true });
    await updateDoc(dataRef, {
      "totals.totalExpenses": increment(numAmount),
    }).catch(() => {
      setDoc(
        dataRef,
        {
          totals: {
            totalIncome: 0,
            totalExpenses: numAmount,
            totalAllocated: 0,
          },
        },
        { merge: true }
      );
    });
  };

  const deleteTransaction = async (transactionId) => {
    if (!currentUser) return;
    const itemToDelete = history.find((h) => h.id === transactionId);
    if (!itemToDelete) return;

    const newHistory = history.filter((h) => h.id !== transactionId);
    const dataRef = doc(db, "users", currentUser.uid, "finance", "data");

    await setDoc(dataRef, { history: newHistory }, { merge: true });

    const incrementPath =
      itemToDelete.type === "income"
        ? "totals.totalIncome"
        : "totals.totalExpenses";
    await updateDoc(dataRef, {
      [incrementPath]: increment(-Number(itemToDelete.amount)),
    });
  };

  const editTransaction = async (transactionId, updatedData) => {
    if (!currentUser) return;
    const normalizedAmount = Math.abs(Number(updatedData.amount));
    if (normalizedAmount > MAX_AMOUNT)
      return alert(`Maximum amount is ${MAX_AMOUNT}`);

    const oldItem = history.find((h) => h.id === transactionId);
    if (!oldItem) return;

    const patchedData = { ...updatedData, amount: normalizedAmount };
    const newHistory = history.map((h) =>
      h.id === transactionId ? { ...h, ...patchedData } : h
    );
    const dataRef = doc(db, "users", currentUser.uid, "finance", "data");

    await setDoc(dataRef, { history: newHistory }, { merge: true });

    // Sync Totals: Subtract old amount, add new amount
    if (oldItem.type === "income") {
      await updateDoc(dataRef, {
        "totals.totalIncome": increment(
          normalizedAmount - Number(oldItem.amount)
        ),
      });
    } else {
      await updateDoc(dataRef, {
        "totals.totalExpenses": increment(
          normalizedAmount - Number(oldItem.amount)
        ),
      });
    }
  };

  const addCategory = async (name) => {
    await addDoc(
      collection(
        doc(db, "users", currentUser.uid, "finance", "data"),
        "categories"
      ),
      { name }
    );
  };

  const deleteCategory = async (id) => {
    await deleteDoc(
      doc(
        doc(db, "users", currentUser.uid, "finance", "data"),
        "categories",
        id
      )
    );
  };

  return (
    <FinanceContext.Provider
      value={{
        loading,
        history,
        categories,
        addIncome,
        addSpending,
        addCategory,
        deleteCategory,
        expenseLimit,
        updateExpenseLimit,
        deleteExpenseLimit,
        deleteTransaction,
        editTransaction,
      }}
    >
      {children}
    </FinanceContext.Provider>
  );
}

export const useFinance = () => useContext(FinanceContext);
